import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, Link,
  InputAdornment, Alert, CircularProgress,
} from '@mui/material';
import {
  Email as EmailIcon,
  MailOutline as MailOutlineIcon,
  BoltOutlined as BoltIcon,
  LockResetOutlined as LockResetIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import axios from 'axios';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return setError('Email is required');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return setError('Please enter a valid email address');

    setLoading(true);
    setError('');
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';
      await axios.post(`${apiUrl}/api/users/forgot-password`, { email: email.trim() });
      setSuccess(true);
    } catch (err) {
      console.error('Forgot password error:', err);
      if (err.response?.data?.message) setError(err.response.data.message);
      else if (err.response?.data?.errors) setError(err.response.data.errors[0].msg);
      else setError('An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
      {/* Left marketing panel — desktop only */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
          position: 'relative',
          overflow: 'hidden',
          background: 'var(--gradient-marketing)',
          color: '#fff',
        }}
      >
        <Box sx={{
          position: 'absolute', inset: 0, opacity: 0.5, pointerEvents: 'none',
          background: `
            radial-gradient(circle at 15% 20%, rgba(255,255,255,0.18) 0%, transparent 40%),
            radial-gradient(circle at 85% 75%, rgba(20,184,166,0.3) 0%, transparent 45%),
            radial-gradient(circle at 50% 110%, rgba(139,92,246,0.4) 0%, transparent 50%)
          `,
        }} />

        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            background: 'rgba(255,255,255,0.18)',
            backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(255,255,255,0.25)',
          }}>
            <BoltIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Typography sx={{
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontWeight: 800,
            fontSize: '1.05rem',
            letterSpacing: '-0.02em',
          }}>
            SUPERCHARGEME
          </Typography>
        </Box>

        <Box sx={{ position: 'relative', maxWidth: 480 }}>
          <Typography sx={{
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontSize: { md: '2.5rem', lg: '3rem' },
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
            mb: 2,
          }}>
            Lost your way?<br/>We'll get you back.
          </Typography>
          <Typography sx={{
            fontSize: '1.05rem',
            opacity: 0.85,
            lineHeight: 1.55,
            fontWeight: 400,
          }}>
            Enter your email and we'll send you a secure link to reset your password.
            You'll be back in the command centre in seconds.
          </Typography>
        </Box>

        <Typography sx={{ position: 'relative', fontSize: '0.8rem', opacity: 0.65 }}>
          © {new Date().getFullYear()} SuperChargeMe. All rights reserved.
        </Typography>
      </Box>

      {/* Right form panel */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 6, md: 8 },
          py: 6,
          bgcolor: 'background.default',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <Box sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center', gap: 1.25, mb: 4,
          }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 1.5,
              background: 'var(--gradient-primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <BoltIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Typography sx={{
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              fontWeight: 800,
              fontSize: '1rem',
              letterSpacing: '-0.02em',
            }}>
              SUPERCHARGEME
            </Typography>
          </Box>

          {success ? (
            <Box>
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%',
                background: 'var(--color-secondary-a10)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mb: 3,
              }}>
                <MailOutlineIcon sx={{ color: 'var(--color-secondary)', fontSize: 28 }} />
              </Box>
              <Typography sx={{
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontWeight: 700,
                fontSize: '1.75rem',
                letterSpacing: '-0.025em',
                mb: 1,
              }}>
                Check your email
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 3, fontSize: '0.95rem', lineHeight: 1.6 }}>
                If an account exists for <Box component="strong" sx={{ color: 'text.primary' }}>{email}</Box>, we've sent a password reset link.
                The link will expire in 1 hour.
              </Typography>

              <Box sx={{
                p: 2.5, borderRadius: 2,
                bgcolor: 'var(--color-primary-a05)',
                border: '1px solid var(--color-primary-a10)',
                mb: 4,
              }}>
                <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', lineHeight: 1.7 }}>
                  • Check your inbox and spam folder<br/>
                  • The link expires in 1 hour<br/>
                  • Didn't get it? You can request another
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/login')}
                  startIcon={<ArrowBackIcon />}
                  sx={{ py: 1.3, borderRadius: 2, flex: 1 }}
                >
                  Back to sign in
                </Button>
                <Button
                  variant="contained"
                  onClick={() => { setSuccess(false); setEmail(''); }}
                  sx={{ py: 1.3, borderRadius: 2, flex: 1, fontWeight: 600 }}
                >
                  Send again
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%',
                background: 'var(--color-primary-a10)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mb: 3,
              }}>
                <LockResetIcon sx={{ color: 'var(--color-primary)', fontSize: 28 }} />
              </Box>

              <Typography sx={{
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontWeight: 700,
                fontSize: '1.75rem',
                letterSpacing: '-0.025em',
                mb: 1,
              }}>
                Forgot password?
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 4, fontSize: '0.95rem' }}>
                Enter your email and we'll send you a reset link.
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  required fullWidth autoFocus
                  id="email" label="Email" name="email" type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 3 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    position: 'relative',
                  }}
                >
                  {loading ? (
                    <CircularProgress
                      size={22}
                      sx={{
                        color: 'white',
                        position: 'absolute',
                        top: '50%', left: '50%',
                        marginTop: '-11px', marginLeft: '-11px',
                      }}
                    />
                  ) : 'Send reset link'}
                </Button>

                <Typography sx={{
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                  mt: 4,
                }}>
                  Remember your password?{' '}
                  <Link
                    component={RouterLink}
                    to="/login"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Sign in
                  </Link>
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
